import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './api';
import { toast } from '../utils/toast';
import { extractApiError } from '../utils/apiError';

export const useCreateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (newUserData: Record<string, unknown>) => {
      const { data } = await apiClient.post('/users', newUserData);
      return data;
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

export const useCreateGig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (gigData: Record<string, unknown>) => {
      const { data } = await apiClient.post('/gigs/', gigData);
      return data;
    },
    onSuccess: () => {
      toast.success('Gig published successfully!');
      queryClient.invalidateQueries({ queryKey: ['gigs'] });
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err, 'Failed to publish gig'));
    },
  });
};

export const useUpdateGig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ gigId, data }: { gigId: string; data: Record<string, unknown> }) => {
      const { data: responseData } = await apiClient.patch(`/gigs/${gigId}/`, data);
      return responseData;
    },
    onSuccess: (_data, { gigId }) => {
      toast.success('Gig updated successfully');
      queryClient.invalidateQueries({ queryKey: ['gig', gigId] });
      queryClient.invalidateQueries({ queryKey: ['gigs'] });
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err, 'Failed to update gig'));
    },
  });
};

export const useDeleteGig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (gigId: string) => {
      await apiClient.delete(`/gigs/${gigId}/`);
    },
    onSuccess: () => {
      toast.success('Gig deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['gigs'] });
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err, 'Failed to delete gig'));
    },
  });
};
export const useApplyToGig = () => {
  return useMutation({
    mutationFn: async ({
      gigUuid,
      data,
    }: {
      gigUuid: string;
      data: {
        email: string;
        message_html: string;
        screen_width: number;
        screen_height: number;
        recaptcha_token?: string;
      };
    }) => {
      const { data: responseData } = await apiClient.post(`/gigs/${gigUuid}/applications/`, data);
      return responseData;
    },
    onSuccess: () => {
      toast.success('Application submitted successfully!');
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err, 'Failed to submit application'));
    },
  });
};

/** Accept or reject a gig application */
export const useUpdateGigApplication = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      applicationUuid,
      status,
      notifyApplicant,
    }: {
      applicationUuid: string;
      status: 'accepted' | 'rejected';
      notifyApplicant?: boolean;
    }) => {
      const { data } = await apiClient.patch(`/gigs/applications/${applicationUuid}/`, {
        status,
        notify_applicant: notifyApplicant ?? true,
      });
      return data;
    },
    onSuccess: (_data, { applicationUuid, status }) => {
      toast.success(`Application ${status} successfully!`);
      queryClient.invalidateQueries({ queryKey: ['gig-applications'] });
      queryClient.invalidateQueries({ queryKey: ['gig-application', applicationUuid] });
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err, 'Failed to update application'));
    },
  });
};
